/**
 * Care Concierge grant history (Feature 106).
 *
 * Append-only: originals are never edited and never disappear. Voiding one adds a negative row
 * below it, so the list reads as a ledger rather than a current-state view.
 */

import { Button } from '@/shared/components/ui/button';
import type { SessionGrantRow } from '../../services/careConciergeCreditApi';

type SessionGrantListProps = {
  grants: SessionGrantRow[];
  busyGrantId: string | null;
  onVoid: (grantId: string) => void;
};

const formatDate = (iso: string): string => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

export const SessionGrantList = ({ grants, busyGrantId, onVoid }: SessionGrantListProps) => {
  if (grants.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No sessions granted yet. Add a block above to open this pool.
      </p>
    );
  }

  return (
    <ul className="divide-y divide-border rounded-md border border-border">
      {grants.map((grant) => {
        const isReversal = grant.reversalOfGrantId !== null;

        return (
          <li key={grant.id} className="flex items-start justify-between gap-4 px-3 py-2">
            <div className="min-w-0">
              <p className="text-sm font-medium">
                <span className={isReversal ? 'text-destructive' : undefined}>
                  {grant.sessions > 0 ? `+${grant.sessions}` : grant.sessions}
                </span>{' '}
                {Math.abs(grant.sessions) === 1 ? 'session' : 'sessions'}
                {isReversal && <span className="text-muted-foreground"> · voided a grant</span>}
                {grant.isReversed && <span className="text-muted-foreground"> · voided</span>}
              </p>
              <p className="text-xs text-muted-foreground">
                {formatDate(grant.createdAt)} · {grant.grantedByName}
              </p>
              {grant.note && <p className="mt-1 text-xs text-foreground">{grant.note}</p>}
            </div>

            {!isReversal && !grant.isReversed && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busyGrantId !== null}
                onClick={() => onVoid(grant.id)}
              >
                {busyGrantId === grant.id ? 'Voiding…' : 'Void'}
              </Button>
            )}
          </li>
        );
      })}
    </ul>
  );
};
